import { apiRequest } from "@/network/apiClient";
import { ArrowLeft, BellIcon, Plus, LogOut, UserPlus, Briefcase } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useState, useRef, useEffect } from "react";
import AddNewLead from "@/components/lead-inventory/add_new_lead.jsx";
import AddOpportunityModal from "./referral/AddOpportunityModal";

const pageSubHeader = ({ title, role, isHunter, isFarmer, showBackButton }) => {
  const navigate = useNavigate();

  const [showNewLeadModal, setShowNewLeadModal] = useState(false);
  const [showNewOppModal, setShowNewOppModal] = useState(false);
  const [showNotifications, setShowNotifications] = useState(false);
  const dropdownRef = useRef(null);
  const handleNewLead = () => {
    setShowNewLeadModal(true);
  };
  const handleNewOpp = () => {
    setShowNewOppModal(true);
  };

  const handleSignOut = () => {
    try {
      localStorage.removeItem("userId");
      localStorage.removeItem("role");
      localStorage.removeItem("is_hybrid");
      localStorage.removeItem("secondary_role");
      localStorage.removeItem("token");
      localStorage.removeItem("refresh_token");
      localStorage.removeItem("pkce_verifier");
      localStorage.removeItem("leadId");
      localStorage.removeItem("email");
      window.location.href = `${import.meta.env.VITE_BASE_URL}/logout`;
    } catch (error) {
      console.error("Error during logout", error);
    }
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowNotifications(false);
      }
    };

    if (showNotifications) {
      document.addEventListener("mousedown", handleClickOutside);
    } else {
      document.removeEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showNotifications]);

  return (
    <>
      <div className="flex flex-row items-center justify-between sticky top-0 bg-white border-t border-gray-200 shadow z-50 px-4 py-3 md:px-6 md:py-4 lg:px-8">
        <div className="flex flex-row items-center text-base md:text-lg text-gray-700 font-bold">
          {showBackButton && (
            <button
              className="flex justify-center items-center w-11 h-11 md:w-[34px] md:h-[34px] rounded-full border border-gray-300 bg-white hover:bg-gray-50 transition-colors cursor-pointer mr-3 md:mr-4"
              onClick={() => navigate(-1)}
              aria-label="Go back"
            >
              <ArrowLeft className="w-5 h-5 md:w-5 md:h-5 text-gray-600" />
            </button>
          )}
          <span className="truncate">{title}</span>
        </div>

        <div className="flex flex-row items-center space-x-2 md:space-x-4">
          {/* New Lead Button - UserPlus icon on mobile, Plus + text on desktop */}
          {isHunter || role === "Admin" || role === 'HOD_Hunter' ? (
            <button
              className="bg-[#FEFAD8] text-black font-bold px-3 py-2 md:px-4 rounded-md hover:bg-[#F7E21E] transition-colors min-h-11 md:min-h-0"
              onClick={handleNewLead}
              aria-label="New Lead"
            >
              <div className="flex flex-row items-center gap-1">
                {/* UserPlus icon on mobile (more descriptive) */}
                <UserPlus className="w-5 h-5 sm:hidden" />
                {/* Plus icon + text on desktop */}
                <Plus className="w-5 h-5 hidden sm:inline" />
                <span className="hidden sm:inline">New Lead</span>
              </div>
            </button>
          ) : null}

          {/* New Opportunity Button - Briefcase icon on mobile, Plus + text on desktop */}
          {isFarmer || role === "Admin" ? (
            <button
              className="bg-[#FEFAD8] text-black font-bold px-3 py-2 md:px-4 rounded-md hover:bg-[#F7E21E] transition-colors min-h-11 md:min-h-0"
              onClick={handleNewOpp}
              aria-label="New Opportunity"
            >
              <div className="flex flex-row items-center gap-1">
                {/* Briefcase icon on mobile (represents business opportunity) */}
                <Briefcase className="w-5 h-5 sm:hidden" />
                {/* Plus icon + text on desktop */}
                <Plus className="w-5 h-5 hidden sm:inline" />
                <span className="hidden sm:inline">New Opportunity</span>
              </div>
            </button>
          ) : null}

          {/* Notifications */}
          <div className="relative" ref={dropdownRef}>
            <button
              className="cursor-pointer flex justify-center items-center w-11 h-11 md:w-[34px] md:h-[34px] rounded-full border-2 border-gray-200 bg-white hover:bg-gray-50 transition-colors"
              onClick={() => setShowNotifications(!showNotifications)}
              aria-label="Notifications"
            >
              <div className="relative group">
                <BellIcon
                  className="w-5 h-5 md:w-4 md:h-4 text-gray-600"
                  strokeWidth={2.5}
                  style={{ fill: "#c5c5c5ff" }}
                />
                <span className="hidden md:block absolute left-1/2 -translate-x-1/2 mt-2 px-2 py-1 text-xs text-white bg-gray-800 rounded opacity-0 group-hover:opacity-100 transition-opacity duration-300 whitespace-nowrap pointer-events-none">
                  Notifications
                </span>
              </div>
              {/* Notification bubble */}
              {/* <span
                className="absolute top-0 right-0 inline-flex items-center justify-center 
                           w-4 h-4 text-[10px] font-bold text-white bg-red-600 
                           rounded-full transform translate-x-1 -translate-y-1"
              >
                3
              </span> */}
            </button>

            {/* Notification dropdown - Responsive width */}
            {showNotifications && (
              <div className="absolute right-0 mt-2 w-[280px] md:w-64 bg-white border border-gray-200 rounded-lg shadow-lg z-50">
                <div className="p-3 text-sm text-gray-700 font-semibold border-b">
                  Notifications
                </div>
                <ul className="divide-y divide-gray-100">
                  <li className="p-3 hover:bg-gray-50 cursor-pointer text-sm">
                    No new notifications for now
                  </li>
                </ul>
              </div>
            )}
          </div>

          {/* Logout */}
          <button
            className="relative group w-11 h-11 md:w-auto md:h-auto flex items-center justify-center hover:bg-gray-100 rounded-full md:rounded-none transition-colors"
            onClick={handleSignOut}
            aria-label="Logout"
          >
            <LogOut
              className="w-6 h-6 hover:text-yellow-300 transition-colors cursor-pointer"
              strokeWidth={2.5}
              style={{ fill: "#c5c5c5ff" }}
            />
            <span className="hidden md:block absolute left-1/2 -translate-x-1/2 mt-2 px-2 py-1 text-xs text-white bg-gray-800 rounded opacity-0 group-hover:opacity-100 transition-opacity duration-300 whitespace-nowrap pointer-events-none">
              Logout
            </span>
          </button>
        </div>
      </div>
      <AddNewLead
        isOpen={showNewLeadModal}
        onClose={() => setShowNewLeadModal(false)}
      />
      <AddOpportunityModal
        isOpen={showNewOppModal}
        onClose={() => setShowNewOppModal(false)}
      />
    </>
  );
};

export default pageSubHeader;
